import mergeDeep from 'deepmerge';

/**
 * @desc A simple wrapper around a componentSchema & layoutComponentMappingSchema to provide higher level access.
 */
class ComponentSchemaModel {

    /**
     * @constructor
     * @desc Initialize the ComponentSchemaModel from schemas
     * @param {Object} componentSchema - The component schema object
     * @param {Object} whiteLabelSchemaOverrides - The component schema overrides object which provides new content etc if this app is for a whitelabel
     *
     */
    constructor(componentSchema, whiteLabelSchemaOverrides) {
        this.componentSchema = componentSchema;
        this.whiteLabelSchemaOverrides = whiteLabelSchemaOverrides;
    }

    /**
     * When extending components, we don't want array props to be concatenated. We just want the source array
     * @see EVT-1882
     * @param {Array} destinationArray The original array
     * @param {Array} sourceArray The latest array in the merge
     * @returns {Array} An array
     * @private
     */
    _useSourceArray = (destinationArray, sourceArray) => sourceArray;

    /**
     * @desc Get a component schema based on its key, it also deep merge with base component schema if you specify "extends" in the schema
     * @param {String} componentKey - the identifier of a component schema
     * @returns {Object} component schema model that defines how the component should be rendered
     */
    getMergedSchema = (componentKey) => {
        const componentSchema = this.getComponentSchema(componentKey);
        // if no extends defined in schema return as is
        if (!componentSchema || !componentSchema.extends) {
            return componentSchema;
        }
        // We should only ever extend from the core set of components, to ensure this is the case, get the baseComponentSchema
        // from this.componentSchema, without going through getComponentSchema, as getComponentSchema will ALWAYS return the
        // component schema from the whitelabel overrides first, so if you have a whitelabel component that extends another component
        // using getComponentSchema will result in a recursion error as it keeps getting itself, not the component schema it's extending
        let baseComponentSchema = this.componentSchema[componentSchema.extends];
        if (baseComponentSchema.extends) {
            // If we're extending again, merge it with the current schema as well, so that we get the values from the inter-mediary extensions.
            baseComponentSchema = mergeDeep(this.getMergedSchema(baseComponentSchema.extends), baseComponentSchema, { arrayMerge: this._useSourceArray });
        }
        // Convert both schemas to immutable then use map.mergeDeep to recursively merge two schemas
        return mergeDeep(baseComponentSchema, componentSchema, { arrayMerge: this._useSourceArray });
    };

    /**
     * Helper function to get the correct schema from either a white label override file, or component schema.
     * @param {String} componentKey - the identifier of a component schema
     * @returns {Object} The schema
     */
    getComponentSchema = (componentKey) => {
        const whiteLabelOverride = this.whiteLabelSchemaOverrides[componentKey] || false;
        return whiteLabelOverride || this.componentSchema[componentKey];
    };

    /**
     * Determines if a component schema exists
     * @param {String} componentKey - the identifier of a component schema
     * @returns {Boolean} - True if the schema exists, false if not.
     */
    componentSchemaExists = (componentKey) => {
        return !!this.getComponentSchema(componentKey);
    };

}

export { ComponentSchemaModel };
